<template>
  <table id="kye-month-table3" @click="handleMonthTableClick" @mousemove="handleMouseMove" class="el-month-table">
    <tbody>
      <tr v-for="(row, key) in rows" :key="key">
        <td :class="getCellStyle(cell)" v-for="(cell, key) in row" :key="key">
          <div>
            <a class="cell">{{ t('el.datepicker.months.' + months[cell.text]) }}</a>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script type="text/babel">
import Locale from 'element-ui/lib/mixins/locale'
import { isDate, range, getDayCountOfMonth, nextDate } from '../util'
import { hasClass } from 'element-ui/lib/utils/dom'
import { arrayFindIndex, coerceTruthyValueToArray, arrayFind } from 'element-ui/lib/utils/util'

const datesInMonth = (year, month) => {
  const numOfDays = getDayCountOfMonth(year, month)
  const firstDay = new Date(year, month, 1)
  return range(numOfDays).map(n => nextDate(firstDay, n))
}

const clearDate = date => {
  return new Date(date.getFullYear(), date.getMonth())
}

const getMonthTimestamp = function (time) {
  if (typeof time === 'number' || typeof time === 'string') {
    return clearDate(new Date(time)).getTime()
  } else if (time instanceof Date) {
    return clearDate(time).getTime()
  } else {
    return NaN
  }
}

// remove the first element that satisfies `pred` from arr
// return a new array if modification occurs
// return the original array otherwise
const removeFromArray = function (arr, pred) {
  const idx = typeof pred === 'function' ? arrayFindIndex(arr, pred) : arr.indexOf(pred)
  return idx >= 0 ? [...arr.slice(0, idx), ...arr.slice(idx + 1)] : arr
}
export default {
  props: {
    disabledDate: {},
    cellClassName: {},
    value: {},
    selectionMode: {
      default: 'month'
    },
    minDate: {},

    maxDate: {},
    defaultValue: {
      validator(val) {
        // null or valid Date Object
        return val === null || isDate(val) || (Array.isArray(val) && val.every(isDate))
      }
    },
    date: {},
    rangeState: {
      default() {
        return {
          endDate: null,
          selecting: false
        }
      }
    }
  },

  mixins: [Locale],

  watch: {
    'rangeState.endDate'(newVal) {
      this.markRange(this.minDate, newVal)
    },

    minDate(newVal, oldVal) {
      if (getMonthTimestamp(newVal) !== getMonthTimestamp(oldVal)) {
        this.markRange(this.minDate, this.maxDate)
      }
    },

    maxDate(newVal, oldVal) {
      if (getMonthTimestamp(newVal) !== getMonthTimestamp(oldVal)) {
        this.markRange(this.minDate, this.maxDate)
      }
    }
  },

  data() {
    return {
      months: ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'],
      tableRows: [[], [], [], []],
      lastRow: null,
      lastColumn: null
    }
  },

  methods: {
    cellMatchesDate(cell, date) {
      const value = new Date(date)
      return this.date.getFullYear() === value.getFullYear() && Number(cell.text) === value.getMonth()
    },
    getCellStyle(cell) {
      const style = {}
      const year = this.date.getFullYear()
      const today = new Date()
      const month = cell.text
      const defaultValue = this.defaultValue
        ? Array.isArray(this.defaultValue)
          ? this.defaultValue
          : [this.defaultValue]
        : []
      style.disabled =
        typeof this.disabledDate === 'function' ? datesInMonth(year, month).every(this.disabledDate) : false
      style.current =
        arrayFindIndex(
          coerceTruthyValueToArray(this.value),
          date => date.getFullYear() === year && date.getMonth() === month
        ) >= 0
      style.today = today.getFullYear() === year && today.getMonth() === month
      style.default = defaultValue.some(date => this.cellMatchesDate(cell, date))

      if (cell.inRange) {
        style['in-range'] = true

        if (cell.start) {
          style['start-date'] = true
        }

        if (cell.end) {
          style['end-date'] = true
        }
      }
      let customClass = cell.cellClassName
      if (typeof cell.cellClassName === 'function') {
        customClass = cell.cellClassName(cell.cellDate)
      }
      if (customClass) {
        style[customClass] = true
      }
      return style
    },
    getMonthOfCell(month) {
      const year = this.date.getFullYear()
      return new Date(year, month, 1)
    },
    markRange(minDate, maxDate) {
      minDate = getMonthTimestamp(minDate)
      maxDate = getMonthTimestamp(maxDate) || minDate
      ;[minDate, maxDate] = [Math.min(minDate, maxDate), Math.max(minDate, maxDate)]
      const rows = this.rows
      for (let i = 0, k = rows.length; i < k; i++) {
        const row = rows[i]
        for (let j = 0, l = row.length; j < l; j++) {
          const cell = row[j]
          const index = i * 3 + j
          const time = new Date(this.date.getFullYear(), index).getTime()

          cell.inRange = minDate && time >= minDate && time <= maxDate
          cell.start = minDate && time === minDate
          cell.end = maxDate && time === maxDate
        }
      }
    },
    handleMouseMove(event) {
      let target = event.target
      if (target.tagName === 'A') {
        target = target.parentNode.parentNode
      }
      if (target.tagName === 'DIV') {
        target = target.parentNode
      }
      if (target.tagName !== 'TD') return

      const row = target.parentNode.rowIndex
      const column = target.cellIndex
      const month = row * 3 + column
      const newDate = this.getMonthOfCell(month)
      const params = {
        newDate,
        event
      }

      if (!this.rangeState.selecting) {
        this.$parent.handleDateMouseEnter && this.$parent.handleDateMouseEnter(params)
        return
      }

      // can not select disabled date
      if (this.rows[row][column].disabled) return

      // only update rangeState when mouse moves to a new cell
      // this avoids frequent Date object creation and improves performance
      if (row !== this.lastRow || column !== this.lastColumn) {
        this.lastRow = row
        this.lastColumn = column
        this.$emit('changerange', {
          minDate: this.minDate,
          maxDate: this.maxDate,
          rangeState: {
            selecting: true,
            endDate: newDate
          }
        })
      }
      this.$parent.handleDateMouseEnter && this.$parent.handleDateMouseEnter(params)
    },
    handleMonthTableClick(event) {
      let target = event.target
      if (target.tagName === 'A') {
        target = target.parentNode.parentNode
      }
      if (target.tagName === 'DIV') {
        target = target.parentNode
      }
      if (target.tagName !== 'TD') return
      if (hasClass(target, 'disabled')) return
      const column = target.cellIndex
      const row = target.parentNode.rowIndex
      const month = row * 3 + column
      const newDate = this.getMonthOfCell(month)
      if (this.selectionMode === 'range') {
        if (!this.rangeState.selecting) {
          this.$emit('pick', { minDate: newDate, maxDate: null })
          this.rangeState.selecting = true
        } else {
          if (newDate >= this.minDate) {
            this.$emit('pick', { minDate: this.minDate, maxDate: newDate })
          } else {
            this.$emit('pick', { minDate: newDate, maxDate: this.minDate })
          }
          this.rangeState.selecting = false
        }
      } else if (this.selectionMode === 'months') {
        const value = this.value || []
        const year = this.date.getFullYear()
        const newValue =
          arrayFindIndex(value, date => date.getFullYear() === year && date.getMonth() === month) >= 0
            ? removeFromArray(value, date => date.getTime() === newDate.getTime())
            : [...value, newDate]
        this.$emit('pick', newValue)
      } else {
        this.$emit('pick', month)
      }
    }
  },

  computed: {
    rows() {
      // TODO: refactory rows / getCellClasses
      const rows = this.tableRows
      const disabledDate = this.disabledDate
      const cellClassName = this.cellClassName
      const selectedDate = []
      const now = getMonthTimestamp(new Date())

      for (let i = 0; i < 4; i++) {
        const row = rows[i]
        for (let j = 0; j < 3; j++) {
          let cell = row[j]
          if (!cell) {
            cell = { row: i, column: j, type: 'normal', inRange: false, start: false, end: false }
          }

          cell.type = 'normal'

          const index = i * 3 + j
          const time = new Date(this.date.getFullYear(), index).getTime()
          cell.inRange = time >= getMonthTimestamp(this.minDate) && time <= getMonthTimestamp(this.maxDate)
          cell.start = this.minDate && time === getMonthTimestamp(this.minDate)
          cell.end = this.maxDate && time === getMonthTimestamp(this.maxDate)
          const isToday = time === now

          if (isToday) {
            cell.type = 'today'
          }
          cell.text = index
          let cellDate = new Date(time)
          cell.disabled = typeof disabledDate === 'function' && disabledDate(cellDate)
          cell.selected = arrayFind(selectedDate, date => date.getTime() === cellDate.getTime())
          cell.cellClassName = cellClassName

          this.$set(row, j, cell)
        }
      }
      return rows
    }
  }
}
</script>
<style lang="scss">
#kye-month-table3 {
  font-size: 12px;
  margin: -1px;
  border-collapse: collapse;

  td {
    text-align: center;
    padding: 8px 0px;
    cursor: pointer;
    & div {
      height: 32px;
      padding: 4px 0;
      box-sizing: border-box;
    }
    &.today {
      .cell {
        color: #8b60f0;
        // font-weight: bold;
        border: 1px solid #8b60f0;
        border-radius: 4px;
        box-sizing: border-box;
      }
      &.start-date .cell,
      &.end-date .cell {
        color: #fff;
      }
    }

    &.disabled .cell {
      // background-color: $--background-color-base;
      cursor: not-allowed;
      // color: $--color-text-placeholder;

      &:hover {
        // color: $--color-text-placeholder;
      }
    }

    .cell {
      width: 60px;
      height: 24px;
      display: block;
      line-height: 24px;
      color: #333333;
      margin: 0 auto;
      border-radius: 4px;
      &:hover {
        color: #8b60f0;
        border-radius: 4px;
        /* C06-Light gray(灰色交互反馈色)/04-浅色背景悬停色(4%) */
        background: rgba(28, 30, 35, 0.04);
      }
    }

    &.in-range div {
      background-color: #F4F0FF;
      &:hover {
        // background-color: $--datepicker-inrange-hover-background-color;
      }
    }
    &.start-date div,
    &.end-date div {
      color: #fff;
    }

    &.start-date .cell,
    &.end-date .cell {
      color: #fff;
      border: none !important;
      background: linear-gradient(90deg, #8b60f0 0%, #8b60f0 100%);
    }
    &.start-date:hover .cell,
    &.end-date:hover .cell {
      color: #8b60f0;
      background: rgba(28, 30, 35, 0.04);
    }

    &.start-date div {
      // margin-left: 12px;
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }

    &.end-date div {
      // margin-right: 12px;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
      span {
        color: #fff !important;
      }
    }

    &.current:not(.disabled) .cell {
      color: #8b60f0;
    }
  }
  td.current {
    div .cell {
      color: #fff !important;
      border: none !important;
      background: linear-gradient(90deg, #8b60f0 0%, #8b60f0 100%);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/public/components/kye-date-picker3/src/basic/month-table.vue